/** @jsx jsx */
import { jsx } from "@emotion/react";
import Axios from "axios";

import Html from "../../../../../../../plugins/@whitespace/gatsby-theme-wordpress-basic/src/wsui/components/Html.jsx";
import {
  Box as DefaultBox,
  TypographyBlock,
  handleComponentsProp,
  useThemeProps,
} from "../../../../../../../wsui/base";
import { FormModule as WsuiFormModule } from "../../../../../../../wsui/municipio";
import ModuleWrapper from "../ModuleWrapper.jsx";

const axios = Axios.create({
  baseURL:
    process.env.GATSBY_WORDPRESS_JSON_URL ||
    process.env.GATSBY_WORDPRESS_URL + "/wp-json",
});

const RECAPTCHA_ACTION = "modularity_form";
let recaptchaScriptPromise;

function loadRecaptcha(siteKey) {
  if (window.grecaptcha) {
    return Promise.resolve(window.grecaptcha);
  }

  if (!recaptchaScriptPromise) {
    recaptchaScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
        siteKey,
      )}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.grecaptcha);
      script.onerror = () => reject(new Error("Could not load reCAPTCHA"));
      document.head.appendChild(script);
    });
  }

  return recaptchaScriptPromise;
}

async function createRecaptchaToken(siteKey) {
  if (!siteKey) {
    throw new Error("The reCAPTCHA site key is not configured");
  }

  const recaptcha = await loadRecaptcha(siteKey);
  await new Promise((resolve) => recaptcha.ready(resolve));
  return recaptcha.execute(siteKey, { action: RECAPTCHA_ACTION });
}

export default function FormModule(props) {
  props = useThemeProps({ props, name: "MunicipioFormModuleController" });
  let { module, components, ...restProps } = props;
  let {
    databaseId,
    nonce,
    recaptchaSiteKey,
    title,
    modFormOptions: {
      formFields,
      submissionNotice,
      submitButtonText,
      submissionPublicAct,
      submissionPublicActContent,
      gdprComplienceNotice,
      gdprComplienceNoticeContent,
    },
  } = module;
  components = handleComponentsProp(components, {
    DisclaimerWrapper: DefaultBox,
    DisclaimerTypographyBlock: TypographyBlock,
  });
  const { DisclaimerWrapper, DisclaimerTypographyBlock } = components;

  let disclaimer =
    (submissionPublicAct && submissionPublicActContent) ||
    (gdprComplienceNotice && gdprComplienceNoticeContent) ? (
      <DisclaimerWrapper>
        <DisclaimerTypographyBlock>
          {submissionPublicAct && submissionPublicActContent ? (
            <p>{submissionPublicActContent}</p>
          ) : null}
          {gdprComplienceNotice && gdprComplienceNoticeContent ? (
            <Html>{gdprComplienceNoticeContent}</Html>
          ) : null}
        </DisclaimerTypographyBlock>
      </DisclaimerWrapper>
    ) : null;

  return (
    <ModuleWrapper title={title} {...restProps}>
      <WsuiFormModule
        fields={formFields.map(({ fieldGroupName, ...field }) => ({
          fieldType: fieldGroupName
            .replace("ModForm_Modformoptions_FormFields_", "")
            .replace(/^./, (str) => str.toLowerCase()),
          ...field,
          // For customContent fields:
          content: field.content && <Html>{field.content}</Html>,
        }))}
        disclaimer={disclaimer}
        submitButtonText={submitButtonText}
        submissionNotice={
          submissionNotice && (
            <TypographyBlock>
              <Html>{submissionNotice}</Html>
            </TypographyBlock>
          )
        }
        onSubmit={async (values, { setStatus, resetForm }) => {
          try {
            values["g-recaptcha-response"] = await createRecaptchaToken(
              recaptchaSiteKey,
            );
            values["modularity-form"] = nonce;
            values["modularity-form-id"] = databaseId;
            values["modularity-form-post-type"] = "form-submissions";
            values["modularity-form-url"] = window.location.href;
            // modularity-gdpr-data
            // modularity-form-history
            values["_wp_http_referer"] = window.location.href;

            const result = await axios({
              method: "post",
              data: values,
              url: `/modularity/mod-form/`,
            });

            if (result.data.status !== "Success") {
              throw new Error("Form submission failed");
            }

            resetForm();
            setStatus({
              success: true,
              failure: false,
            });
          } catch (error) {
            console.error("Could not submit form", error);
            setStatus({
              success: false,
              failure: true,
            });
          }
        }}
        formProps={{ id: `modularity-form-${databaseId}` }}
        {...restProps}
      />
    </ModuleWrapper>
  );
}
